import { useState, useEffect, useRef, useCallback } from 'react';
import { getSettings, updateSettings, getUserProfile, getSubscriptionLimits, getVehicles, getGarages, getLegalAcceptances } from '../../utils/storage';
import LegalModal, { LEGAL_VERSION } from '../LegalModal';
import ContactSupportModal from './ContactSupportModal';
import PricingModal from '../PricingModal';
import EditProfileModal from './EditProfileModal';
import { getPortalUrl, redirectToPortal } from '../../services/stripeService';
import { updateUserProfile } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import { isNativeApp, isIOS, isAndroid, getAccountUrl } from '../../utils/platform';
import { isIAPAvailable, restoreIAPPurchases } from '../../services/iapService';
import { syncAllBackupData, downloadExportAsJson } from '../../services/backupService';

export default function Settings({ onReset, onToast, onAdmin, onSuperAdmin, onAccount, onClubsChanged, onClubAdmin, isClubOwner, onClubPricing }) {
  const { signOut, user, isDemoMode } = useAuth();
  const [settings, setSettings] = useState(() => getSettings());
  const userProfile = getUserProfile();
  const [showReset, setShowReset] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [showLegal, setShowLegal] = useState(null); // 'terms' | 'privacy' | 'safety' | null
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [emailDigest, setEmailDigest] = useState(true);
  const [emailDigestLoading, setEmailDigestLoading] = useState(false);
  const [backupSyncing, setBackupSyncing] = useState(false);
  const [backupLastSynced, setBackupLastSynced] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load email digest preference from Supabase on mount
  useEffect(() => {
    if (!user?.id || isDemoMode) return;
    supabase
      .from('users')
      .select('email_digest')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data && data.email_digest !== undefined) {
          setEmailDigest(data.email_digest);
        }
      });
  }, [user?.id]);

  // Load backup last-synced timestamp on mount (paid users only)
  useEffect(() => {
    if (!user?.id || isDemoMode || settings.subscription?.plan === 'free') return;
    supabase
      .from('user_data_backup')
      .select('updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.updated_at) setBackupLastSynced(data.updated_at);
      });
  }, [user?.id, settings.subscription?.plan]);

  async function handleSyncNow() {
    if (!user?.id || backupSyncing) return;
    setBackupSyncing(true);
    try {
      const result = await syncAllBackupData(user.id);
      setBackupLastSynced(new Date().toISOString());
      onToast(`Backup complete — ${result.synced} data types synced`);
    } catch (err) {
      onToast('Backup failed — please try again');
      if (import.meta.env.DEV) console.error('[Settings] Sync error:', err);
    } finally {
      setBackupSyncing(false);
    }
  }

  function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const { sizeBytes } = downloadExportAsJson();
      const sizeMB = (sizeBytes / (1024 * 1024)).toFixed(1);
      onToast(`Data exported (${sizeMB} MB)`);
    } catch (err) {
      onToast('Export failed');
      if (import.meta.env.DEV) console.error('[Settings] Export error:', err);
    } finally {
      setExporting(false);
    }
  }

  function formatBackupDate(isoStr) {
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)} min ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)} hours ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Admin triple-tap on "Settings" title
  const adminTapRef = useRef({ count: 0, timer: null });
  function handleTitleTap() {
    if (user?.email !== 'carsten@propermiles.com' || !onAdmin) return;
    const t = adminTapRef.current;
    t.count += 1;
    clearTimeout(t.timer);
    if (t.count >= 3) {
      t.count = 0;
      onAdmin();
    } else {
      t.timer = setTimeout(() => { t.count = 0; }, 600);
    }
  }

  // Easter egg: 7 clicks within 2 seconds on the gear
  const clickTimesRef = useRef([]);
  const handleGearClick = useCallback(() => {
    const now = Date.now();
    clickTimesRef.current.push(now);
    // Keep only clicks within last 2 seconds
    clickTimesRef.current = clickTimesRef.current.filter((t) => now - t < 2000);
    if (clickTimesRef.current.length >= 7) {
      setShowResetButton(true);
      clickTimesRef.current = [];
      onToast('Developer mode activated');
    }
  }, [onToast]);

  function updatePref(field, value) {
    const updated = updateSettings({ preferences: { [field]: value } });
    setSettings(updated);
  }

  async function toggleEmailDigest() {
    if (!user?.id || emailDigestLoading || isDemoMode) return;
    const newValue = !emailDigest;
    setEmailDigest(newValue);
    setEmailDigestLoading(true);
    try {
      await updateUserProfile(user.id, { email_digest: newValue });
    } catch (err) {
      // Revert on failure
      setEmailDigest(!newValue);
      onToast('Failed to update notification preference');
      if (import.meta.env.DEV) console.error('[Settings] email_digest toggle error:', err);
    } finally {
      setEmailDigestLoading(false);
    }
  }

  const { profile, subscription, preferences } = settings;
  const displayName = userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : profile.name;
  const displayEmail = userProfile ? userProfile.email : profile.email;
  const usagePercent = Math.min(100, Math.round((subscription.creditsUsed / subscription.creditsTotal) * 100));
  const legalAcceptances = getLegalAcceptances();

  function formatLegalDate(isoStr) {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="screen" style={{ paddingBottom: 100, overflowY: 'auto' }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <h1 className="screen-title" style={{ flex: 1 }} onClick={handleTitleTap}>Settings</h1>
        <button
          onClick={handleGearClick}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'transparent', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-muted)',
          }}
          aria-label="Settings gear"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </button>
      </div>

      {/* Profile & Account */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          {/* Avatar */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.125rem',
              color: 'white',
              flexShrink: 0,
            }}
          >
            {displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>{displayName}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{displayEmail}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowEditProfile(true)}
            style={{ fontSize: '0.875rem', padding: '10px 16px' }}
          >
            Edit Profile
          </button>
          {isClubOwner && !isDemoMode && onClubAdmin && (
            <button
              className="btn btn-secondary"
              onClick={onClubAdmin}
              style={{ fontSize: '0.875rem', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Club Admin
            </button>
          )}
          {onSuperAdmin && (
            <button
              className="btn btn-secondary"
              onClick={onSuperAdmin}
              style={{ fontSize: '0.875rem', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Super Admin
            </button>
          )}
        </div>
      </div>

      {/* Subscription */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Subscription</h3>
        <div style={{
          padding: '14px 16px',
          borderRadius: 'var(--radius-sm)',
          border: '1.5px solid var(--accent)',
          background: 'var(--accent-subtle)',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: '0.9375rem', textTransform: 'capitalize' }}>
              {subscription.plan}
            </span>
            <span style={{
              fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 4,
              background: 'var(--accent)', color: '#fff',
            }}>
              CURRENT PLAN
            </span>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {subscription.plan === 'free'
              ? '1 vehicle · 1 garage · 15 AI credits/mo'
              : subscription.plan === 'enthusiast'
                ? '5 vehicles · 1 garage · 100 AI credits/mo'
                : subscription.plan === 'collector'
                  ? '15 vehicles · 3 garages · 250 AI credits/mo'
                  : subscription.plan === 'showroom'
                    ? '50 vehicles · 5 garages · 500 AI credits/mo'
                    : 'Unlimited vehicles · Unlimited garages · 500 AI credits/mo'}
          </div>
        </div>

        {isNativeApp ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {subscription.plan === 'free' ? (
              <button
                className="btn btn-primary"
                onClick={() => setShowPricing(true)}
                style={{ fontSize: '0.8125rem', padding: '10px 16px' }}
              >
                Upgrade your plan
              </button>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPricing(true)}
                  style={{ fontSize: '0.8125rem', padding: '10px 16px' }}
                >
                  Change Plan
                </button>
                {/* Deep-link to App Store subscription management */}
                {isIOS ? (
                  <a
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8125rem', padding: '10px 16px', textDecoration: 'none', textAlign: 'center' }}
                  >
                    Manage Subscription
                  </a>
                ) : isAndroid ? (
                  // Android: subs are managed on the web (Stripe), not Play
                  // Billing — open the web account in the system browser.
                  <a
                    href={getAccountUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (typeof window !== 'undefined' && window.__pmOpenExternal) {
                        e.preventDefault();
                        window.__pmOpenExternal(getAccountUrl());
                      }
                    }}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8125rem', padding: '10px 16px', textDecoration: 'none', textAlign: 'center' }}
                  >
                    Manage on the Web
                  </a>
                ) : (
                  <a
                    href="https://play.google.com/store/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8125rem', padding: '10px 16px', textDecoration: 'none', textAlign: 'center' }}
                  >
                    Manage Subscription
                  </a>
                )}
              </>
            )}
            {/* Restore Purchases — required by Apple for subscription apps */}
            {isIAPAvailable() && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '8px 16px' }}
                onClick={async () => {
                  try {
                    onToast('Restoring purchases…');
                    const result = await restoreIAPPurchases();
                    if (result.status === 'completed') {
                      // Refresh subscription from Supabase
                      const { data: profile } = await supabase
                        .from('users')
                        .select('subscription_tier, credits_remaining')
                        .eq('id', user?.id)
                        .maybeSingle();
                      if (profile) {
                        const { syncSubscriptionFromSupabase } = await import('../../utils/storage');
                        syncSubscriptionFromSupabase(profile);
                        setSettings(getSettings());
                      }
                      onToast('Purchases restored!');
                    } else {
                      onToast('No purchases to restore.');
                    }
                  } catch {
                    onToast('Unable to restore purchases. Please try again.');
                  }
                }}
              >
                Restore Purchases
              </button>
            )}
          </div>
        ) : subscription.plan === 'free' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowPricing(true)}
              style={{ fontSize: '0.8125rem', padding: '10px 16px' }}
            >
              Upgrade your plan
            </button>
            {onClubPricing && (
              <button
                className="btn btn-secondary"
                onClick={onClubPricing}
                style={{ width: '100%', fontSize: '0.8125rem', padding: '10px 16px' }}
              >
                See our Club Subscriptions
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPricing(true)}
                style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px' }}
              >
                Change Plan
              </button>
              <button
                className="btn btn-secondary"
                disabled={billingLoading}
                onClick={async () => {
                  setBillingLoading(true);
                  try {
                    await redirectToPortal(user?.email, user?.id);
                  } catch (err) {
                    if (import.meta.env.DEV) console.error('[Settings] Portal error:', err);
                    if (err?.message?.includes('No Stripe customer')) {
                      onToast('Your subscription was created through the App Store. To change your subscription, please use the mobile app.');
                    } else {
                      // Fallback to static portal URL
                      const portalUrl = getPortalUrl();
                      if (portalUrl) {
                        window.open(portalUrl, '_blank');
                      } else {
                        onToast(err.message || 'Unable to open billing portal');
                      }
                    }
                  } finally {
                    setBillingLoading(false);
                  }
                }}
                style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px', opacity: billingLoading ? 0.6 : 1 }}
              >
                {billingLoading ? 'Loading...' : 'Manage Billing'}
              </button>
            </div>
            {onClubPricing && (
              <button
                className="btn btn-secondary"
                onClick={onClubPricing}
                style={{ width: '100%', fontSize: '0.8125rem', padding: '10px 16px' }}
              >
                See our Club Subscriptions
              </button>
            )}
          </div>
        )}
      </div>

      {/* Usage Tracking */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Research Credits</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            {subscription.creditsUsed} of {subscription.creditsTotal} used this month
          </span>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {subscription.creditsTotal - subscription.creditsUsed} remaining
          </span>
        </div>
        {/* Progress bar */}
        <div style={{
          width: '100%',
          height: 8,
          borderRadius: 4,
          background: 'var(--bg-elevated)',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${usagePercent}%`,
            height: '100%',
            borderRadius: 4,
            background: usagePercent > 80 ? 'var(--warning)' : 'var(--accent)',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
          {isNativeApp ? 'Resets monthly.' : 'Resets monthly. Upgrade your plan for more credits.'}
        </p>
      </div>

      {/* Cloud Backup & Export */}
      {!isDemoMode && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={sectionTitleStyle}>Cloud Backup & Export</h3>
          {subscription.plan === 'free' ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
                Your data is stored locally on this device. Upgrade to automatically back up chat histories,
                DIY guides, and more to the cloud.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowPricing(true)}
                style={{ fontSize: '0.8125rem', padding: '10px 16px' }}
              >
                Upgrade to Enable Cloud Backup
              </button>
            </div>
          ) : (
            <>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                {backupLastSynced
                  ? `Last synced: ${formatBackupDate(backupLastSynced)}`
                  : 'Chat histories, DIY guides, and preferences are backed up automatically.'}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-secondary"
                  disabled={backupSyncing}
                  onClick={handleSyncNow}
                  style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px', opacity: backupSyncing ? 0.6 : 1 }}
                >
                  {backupSyncing ? 'Syncing…' : 'Sync Now'}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={exporting}
                  onClick={handleExport}
                  style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px', opacity: exporting ? 0.6 : 1 }}
                >
                  {exporting ? 'Exporting…' : 'Download My Data'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Preferences */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Preferences</h3>

        {/* Units toggle */}
        <div style={prefRowStyle}>
          <span style={prefLabelStyle}>Distance Units</span>
          <div style={{ display: 'flex', gap: 0, borderRadius: 100, overflow: 'hidden', border: '1.5px solid var(--border)' }}>
            {['miles', 'km'].map((unit) => (
              <button
                key={unit}
                onClick={() => updatePref('units', unit)}
                style={{
                  padding: '6px 14px',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  background: preferences.units === unit ? 'var(--accent)' : 'transparent',
                  color: preferences.units === unit ? 'white' : 'var(--text-muted)',
                  transition: 'all 0.15s',
                  textTransform: 'capitalize',
                }}
              >
                {unit === 'miles' ? 'Miles' : 'Km'}
              </button>
            ))}
          </div>
        </div>


      </div>

      {/* Notifications */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Notifications</h3>

        {/* Email sub-section */}
        <div style={{ padding: '0 20px 4px', marginBottom: 4 }}>
          <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Email</span>
        </div>

        <div style={{ ...prefRowStyle, borderBottom: isNativeApp ? '1px solid var(--border)' : 'none', paddingBottom: isNativeApp ? 14 : 0 }}>
          <div style={{ flex: 1 }}>
            <span style={prefLabelStyle}>Daily Reminder Digest</span>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Receive a daily email with upcoming and overdue reminders
            </div>
          </div>
          <button
            onClick={toggleEmailDigest}
            disabled={emailDigestLoading}
            aria-label={emailDigest ? 'Disable daily digest' : 'Enable daily digest'}
            style={{
              width: 48,
              height: 28,
              borderRadius: 14,
              border: 'none',
              background: emailDigest ? 'var(--accent)' : 'var(--bg-elevated)',
              position: 'relative',
              cursor: emailDigestLoading ? 'wait' : 'pointer',
              transition: 'background 0.2s',
              flexShrink: 0,
              opacity: emailDigestLoading ? 0.6 : 1,
            }}
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              position: 'absolute',
              top: 3,
              left: emailDigest ? 23 : 3,
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </button>
        </div>

        {/* Push Notifications sub-section — native app only */}
        {isNativeApp && (
          <>
            <div style={{ padding: '14px 20px 4px', marginBottom: 4 }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Push Notifications</span>
            </div>

            {[
              { field: 'pushReminders', label: 'Reminders', desc: 'Get notified when a reminder is due', supaCol: 'push_reminders' },
              { field: 'pushAnalysisComplete', label: 'Analysis Complete', desc: 'Get notified when a vehicle analysis finishes', supaCol: 'push_analysis_complete' },
              { field: 'pushMileageCheckin', label: 'Mileage Check-in', desc: 'Nudge after 60 days of no mileage update', supaCol: 'push_mileage_checkin' },
              { field: 'pushEventCheckin', label: 'Event Check-in', desc: 'Get notified on event day to check in', supaCol: 'push_event_checkin' },
              { field: 'pushReferralAccepted', label: 'Referral Accepted', desc: 'Get notified when your referral signs up', supaCol: 'push_referral_accepted' },
            ].map((item, i, arr) => {
              const currentVal = preferences[item.field] ?? true;
              return (
                <div key={item.field} style={{ ...prefRowStyle, borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none', paddingBottom: i === arr.length - 1 ? 0 : 14 }}>
                  <div style={{ flex: 1 }}>
                    <span style={prefLabelStyle}>{item.label}</span>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {item.desc}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (isDemoMode) return;
                      const newVal = !currentVal;
                      updatePref(item.field, newVal);
                      if (user?.id) {
                        updateUserProfile(user.id, { [item.supaCol]: newVal }).catch(() => {
                          if (import.meta.env.DEV) console.error('[Push] Failed to sync preference:', item.field);
                        });
                      }
                    }}
                    aria-label={currentVal ? `Disable ${item.label}` : `Enable ${item.label}`}
                    style={{
                      width: 48,
                      height: 28,
                      borderRadius: 14,
                      border: 'none',
                      background: currentVal ? 'var(--accent)' : 'var(--bg-elevated)',
                      position: 'relative',
                      cursor: isDemoMode ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      opacity: isDemoMode ? 0.5 : 1,
                    }}
                  >
                    <div style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 3,
                      left: currentVal ? 23 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Legal Documents */}
      <div className="card" style={{ marginBottom: 12, padding: 0 }}>
        <h3 style={{ ...sectionTitleStyle, padding: '16px 20px 0' }}>Legal</h3>
        {(() => {
          const docs = [
            { label: 'Terms of Service', type: 'terms', acceptedAt: legalAcceptances.termsAcceptedAt, version: legalAcceptances.termsVersion },
            { label: 'Privacy Policy', type: 'privacy', acceptedAt: legalAcceptances.privacyAcceptedAt, version: legalAcceptances.privacyVersion },
            { label: 'Safety Disclaimer', type: 'safety', acceptedAt: legalAcceptances.safetyAcceptedAt, version: legalAcceptances.safetyVersion },
            ...(!isDemoMode ? [{ label: 'Event Liability Disclaimer', type: 'events' }] : []),
          ];
          return docs.map((doc, i) => (
            <button
              key={doc.type}
              onClick={() => setShowLegal(doc.type)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                background: 'none',
                border: 'none',
                borderBottom: i < docs.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>{doc.label}</div>
                {doc.acceptedAt && (
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    Accepted {formatLegalDate(doc.acceptedAt)} · v{doc.version}
                  </div>
                )}
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ));
        })()}
      </div>

      {/* Help & Support */}
      <div className="card" style={{ marginBottom: 12, padding: 0 }}>
        <h3 style={{ ...sectionTitleStyle, padding: '16px 20px 0' }}>Help & Support</h3>
        {[
          { label: 'Contact Support', action: () => setShowContactSupport(true) },
        ].map((item, i, arr) => (
          <button
            key={item.label}
            onClick={item.action}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 20px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              background: 'none',
              border: 'none',
              borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
              cursor: 'pointer',
            }}
          >
            {item.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ))}
      </div>

      {/* Sign Out */}
      <button
        className="btn btn-secondary"
        onClick={() => signOut().catch(() => onToast('Sign out failed'))}
        style={{ marginBottom: 12 }}
      >
        Sign Out
      </button>

      {/* Data & Account Deletion — always visible per Apple guideline 5.1.1(v) */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ ...sectionTitleStyle, color: 'var(--danger, #ef4444)' }}>Data & Account Deletion</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
          Permanently delete your account and all associated data including vehicles, garages, and service history. This action cannot be undone.
        </p>
        <button
          onClick={() => setShowDeleteAccount(true)}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--danger, #ef4444)',
            color: 'var(--danger, #ef4444)',
            fontSize: '0.875rem',
            fontWeight: 600,
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          Delete Account
        </button>
      </div>

      {/* Reset Data — hidden behind 7-click easter egg */}
      {showResetButton && (
        <button
          onClick={() => setShowReset(true)}
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--danger)',
            color: 'var(--danger)',
            fontSize: '0.875rem',
            fontWeight: 600,
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          Reset All Data
        </button>
      )}

      {/* Reset confirmation modal with type-to-confirm */}
      {showReset && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200, padding: 20,
          }}
          onClick={() => { setShowReset(false); setResetConfirmText(''); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-md)',
              padding: 24, width: '100%', maxWidth: 320,
              border: '1px solid var(--border)', textAlign: 'center',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, marginBottom: 8 }}>
              Are you sure you want to delete everything?
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
              This will permanently clear all vehicles, garages, and service history from both local storage and the server. Your account will remain intact.
            </p>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ textAlign: 'center' }}>Type GARAGE to confirm</label>
              <input
                className="form-input"
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="GARAGE"
                style={{ fontFamily: 'monospace', letterSpacing: '0.1em', textAlign: 'center' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowReset(false); setResetConfirmText(''); }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                disabled={resetConfirmText !== 'GARAGE'}
                onClick={async () => {
                  // Delete Supabase data (not the user account)
                  if (user) {
                    try {
                      await supabase.from('vehicles').delete().eq('user_id', user.id);
                      await supabase.from('garages').delete().eq('owner_id', user.id);
                      await supabase.from('users').delete().eq('id', user.id);
                      console.log('Supabase data cleared for user:', user.id);
                    } catch (err) {
                      console.error('Failed to clear Supabase data:', err);
                    }
                  }
                  onReset();
                  setShowReset(false);
                  setResetConfirmText('');
                  setShowResetButton(false);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: resetConfirmText === 'GARAGE' ? '#ef4444' : 'var(--bg-elevated)',
                  color: resetConfirmText === 'GARAGE' ? 'white' : 'var(--text-muted)',
                  cursor: resetConfirmText === 'GARAGE' ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                }}
              >
                Delete Everything
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account confirmation modal */}
      {showDeleteAccount && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 200, padding: 20,
          }}
          onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(''); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-md)',
              padding: 24, width: '100%', maxWidth: 320,
              border: '1px solid var(--border)', textAlign: 'center',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, marginBottom: 8 }}>
              Delete your account?
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
              This will permanently delete your account, all vehicles, garages, service history, and personal data from both this device and our servers. You will be signed out.
            </p>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ textAlign: 'center' }}>Type DELETE to confirm</label>
              <input
                className="form-input"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{ fontFamily: 'monospace', letterSpacing: '0.1em', textAlign: 'center' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(''); }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                onClick={async () => {
                  setDeleteLoading(true);
                  try {
                    // Skip server-side deletion for demo — just clear local data & sign out
                    if (!isDemoMode) {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (session?.access_token) {
                        const res = await fetch(
                          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
                          {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${session.access_token}`,
                              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                              'Content-Type': 'application/json',
                            },
                          },
                        );
                        if (!res.ok) {
                          const errData = await res.json().catch(() => ({}));
                          if (import.meta.env.DEV) console.error('[Settings] Delete account error:', errData);
                        }
                      }
                    }
                  } catch (err) {
                    if (import.meta.env.DEV) console.error('[Settings] Delete account error:', err);
                  }
                  // Always clear local data and sign out, even if server call fails
                  onReset();
                  setShowDeleteAccount(false);
                  setDeleteConfirmText('');
                  setDeleteLoading(false);
                  await signOut().catch(() => {});
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: deleteConfirmText === 'DELETE' && !deleteLoading ? '#ef4444' : 'var(--bg-elevated)',
                  color: deleteConfirmText === 'DELETE' && !deleteLoading ? 'white' : 'var(--text-muted)',
                  cursor: deleteConfirmText === 'DELETE' && !deleteLoading ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  opacity: deleteLoading ? 0.6 : 1,
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legal modal */}
      {showLegal && (
        <LegalModal
          type={showLegal}
          onClose={() => setShowLegal(null)}
        />
      )}

      {/* Contact Support modal */}
      {showContactSupport && (
        <ContactSupportModal
          onClose={() => setShowContactSupport(false)}
          onToast={onToast}
        />
      )}

      {/* Pricing modal */}
      {showPricing && (
        <PricingModal
          onClose={() => {
            setShowPricing(false);
            setSettings(getSettings()); // re-read subscription tier from localStorage
          }}
          onToast={onToast}
        />
      )}

      {/* Edit Profile modal */}
      {showEditProfile && (
        <EditProfileModal
          onClose={() => {
            setShowEditProfile(false);
            // Refresh settings to reflect updated name
            setSettings(getSettings());
          }}
          onToast={onToast}
          userId={user?.id}
        />
      )}
    </div>
  );
}

/* ─── Shared styles ─── */
const sectionTitleStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 12,
};

const prefRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 14,
  marginBottom: 14,
  borderBottom: '1px solid var(--border)',
};

const prefLabelStyle = {
  fontSize: '0.875rem',
  color: 'var(--text-primary)',
};
